import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { FaAd, FaSync, FaEye, FaTimes, FaPlay, FaCamera, FaCalendar, FaUser, FaGlobe, FaVideo, FaClock, FaMoneyBillWave, FaCheckCircle, FaExternalLinkAlt, FaTag, FaPaperPlane, FaCheckDouble } from "react-icons/fa";
import AdsSubNav from '../../Common/AdsSubNav';

const InfluenerCompletedLeads = () => {
  const navigate = useNavigate();

  // State to store the advertisements data
  const [completedAds, setCompletedAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Modal states
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedAd, setSelectedAd] = useState(null);
  const [showScreenshotModal, setShowScreenshotModal] = useState(false);
  const [currentScreenshot, setCurrentScreenshot] = useState("");

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;
  const totalPages = Math.ceil(completedAds.length / rowsPerPage);

  // Get current page data
  const paginatedAds = completedAds.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  // Handle page change
  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  // Fetch advertisements data from API
  const fetchCompletedAds = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/reporter/get/completed/ads`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      console.log("Fetched Completed Ads:", response);
      // Sort ads by submittedAt (newest first)
      const sortedAds = [...response.data.data].sort((a, b) => {
        const aDate = a.proofs && a.proofs[0]?.submittedAt ? new Date(a.proofs[0].submittedAt) : new Date(0);
        const bDate = b.proofs && b.proofs[0]?.submittedAt ? new Date(b.proofs[0].submittedAt) : new Date(0);
        return bDate - aDate;
      });
      setCompletedAds(sortedAds);
    } catch (err) {
      setError("Failed to fetch advertisements");
      console.error("Error fetching advertisements:", err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch data when component mounts
  useEffect(() => {
    fetchCompletedAds();
  }, []);

  // Modal functions
  const openDetailsModal = (ad) => {
    setSelectedAd(ad);
    setShowDetailsModal(true);
  };

  const closeDetailsModal = () => {
    setShowDetailsModal(false);
    setSelectedAd(null);
  };

  const openScreenshotModal = (screenshotUrl) => {
    setCurrentScreenshot(screenshotUrl);
    setShowScreenshotModal(true);
  };

  const closeScreenshotModal = () => {
    setShowScreenshotModal(false);
    setCurrentScreenshot("");
  };

  // Helper function to format date
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Helper function to get screenshot URL
  const getScreenshotUrl = (screenshot) => {
    if (!screenshot) return "";
    return screenshot.startsWith('http')
      ? screenshot
      : `${process.env.REACT_APP_API_BASE_URL}/${screenshot.replace(/\\/g, '/')}`;
  };



  return (
    <div className="container mx-auto p-2 sm:p-3 md:p-4 lg:p-6">
      <div className="space-y-3 sm:space-y-4 md:space-y-6">

        <AdsSubNav type="paid" />

        {/* Modern Header - Ultra Compact */}
        <div className="mb-4 sm:mb-5">
          <div className="bg-gradient-to-r from-purple-600 via-violet-600 to-fuchsia-600 rounded-xl sm:rounded-[1.5rem] shadow-lg p-2.5 sm:p-4 text-white">
            <div className="flex flex-row items-center justify-between gap-2 sm:gap-4">

              <div className="flex items-center gap-2 sm:gap-3">
                <button
                  onClick={() => navigate(-1)}
                  className="hidden sm:flex px-2.5 py-1 rounded-full bg-white/10 hover:bg-white/20 text-white text-[10px] sm:text-xs font-black uppercase tracking-wider border border-white/20 transition-all whitespace-nowrap"
                >
                  ← Back
                </button>
                <div className="p-1.5 sm:p-2 bg-white/20 rounded-lg backdrop-blur-sm shadow-inner flex-shrink-0">
                  <FaCheckDouble className="text-[12px] sm:text-base text-white" />
                </div>
                <div className="flex flex-col justify-center">
                  <h1 className="text-xs sm:text-lg font-black tracking-tight leading-none mb-0.5 sm:mb-1">Done Ads</h1>
                  <p className="text-purple-50 text-[8px] sm:text-[10px] font-bold uppercase tracking-widest leading-none">Completed: {completedAds.length}</p>
                </div>
              </div>

              <div className="flex items-center gap-2 sm:gap-3">
                <div className="hidden sm:flex flex-col items-end pr-3 border-r border-white/20">
                  <div className="text-sm sm:text-base font-black leading-none">{completedAds.length}</div>
                  <div className="text-[8px] sm:text-[9px] text-purple-50 uppercase tracking-widest font-bold">Confirmed</div>
                </div>
                <button
                  className="flex items-center justify-center gap-1.5 bg-white/20 hover:bg-white/30 px-3 py-1.5 rounded-lg transition-colors backdrop-blur-sm text-[10px] sm:text-xs tracking-wider font-black uppercase shadow-sm"
                  onClick={fetchCompletedAds}
                  disabled={loading}
                >
                  <FaSync className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} />
                  <span className="hidden sm:inline">Refresh</span>
                </button>
              </div>

            </div>
          </div>
        </div>

        {/* Advertisements Table */}
        <div className="bg-white rounded-lg sm:rounded-xl md:rounded-2xl shadow-xl border border-gray-200">

          {/* Mobile View - Compact Cards */}
          <div className="sm:hidden p-3 space-y-3">
            {loading ? (
              Array(3).fill(0).map((_, i) => (
                <div key={i} className="h-28 bg-slate-50 animate-pulse rounded-xl" />
              ))
            ) : completedAds.length === 0 ? (
              <div className="max-w-xl mx-auto text-center py-10 px-4 animate-fadeIn">
                <div className="relative inline-block mb-6">
                  <div className="absolute inset-0 bg-purple-400/20 blur-[40px] rounded-full" />
                  <div className="relative w-32 h-32 rounded-full border-4 border-purple-100 shadow-xl overflow-hidden mx-auto bg-white flex items-center justify-center ring-4 ring-purple-50/50">
                    <FaCheckDouble className="text-purple-200 text-5xl" />
                  </div>
                </div>
                <h3 className="text-slate-900 font-black text-xl tracking-tight mb-1">Success Awaits</h3>
                <p className="text-slate-500 text-xs font-medium leading-relaxed mb-6">
                  Completed advertisements will appear here. Finish your active campaigns to see your results!
                </p>
                <button
                  onClick={fetchCompletedAds}
                  className="bg-purple-600 text-white px-6 py-2 rounded-xl font-black text-[10px] uppercase tracking-widest shadow-lg shadow-purple-100 transition-all active:scale-95"
                >
                  Refresh Data
                </button>
              </div>
            ) : (
              paginatedAds.map((ad, index) => (
                <div key={ad.adId || index} className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden flex h-28 transform transition-all active:scale-[0.98]">
                  {/* Left: Media Thumbnail */}
                  <div className="w-24 bg-slate-100 flex-shrink-0 relative overflow-hidden">
                    {ad.proofs?.[0]?.screenshot ? (
                      <img
                        src={getScreenshotUrl(ad.proofs[0].screenshot)}
                        alt="Ad"
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        onClick={() => openDetailsModal(ad)}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-slate-400 bg-slate-50">
                        <FaAd className="text-xl opacity-20" />
                      </div>
                    )}
                    <div className="absolute top-1 left-1">
                      <span className="px-1.5 py-0.5 rounded text-[8px] font-black uppercase bg-green-500 text-white shadow-sm ring-1 ring-white/20">
                        {ad.status || "Completed"}
                      </span>
                    </div>
                  </div>

                  {/* Right: Details */}
                  <div className="flex-1 p-2.5 flex flex-col justify-between min-w-0">
                    <div className="space-y-1">
                      <div className="flex justify-between items-start gap-1">
                        <h3 className="text-slate-900 font-black text-xs uppercase tracking-tight truncate leading-tight">
                          {ad.adType || "N/A"}
                        </h3>
                        <span className="text-emerald-600 font-black text-[10px] whitespace-nowrap bg-emerald-50 px-1.5 py-0.5 rounded">
                          ₹{ad.finalReporterPrice || 0}
                        </span>
                      </div>
                      <p className="text-slate-400 text-[8px] font-bold uppercase tracking-wider truncate font-mono">
                        ID: {ad.adId?.slice(-8) || "N/A"}
                      </p>

                      <div className="flex items-center gap-1.5 text-slate-500">
                        <div className="w-4 h-4 rounded-full bg-slate-50 flex items-center justify-center border border-slate-100">
                          <FaCalendar className="text-[7px] text-slate-400" />
                        </div>
                        <span className="text-[9px] font-black italic text-slate-400 uppercase tracking-tighter">
                          {ad.proofs?.[0]?.submittedAt ? new Date(ad.proofs[0].submittedAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : "N/A"}
                        </span>
                      </div>
                    </div>

                    <button
                      onClick={() => openDetailsModal(ad)}
                      className="w-full py-2 bg-slate-900 hover:bg-slate-800 text-white text-[9px] font-black uppercase tracking-widest rounded-lg flex items-center justify-center gap-2 transition-all active:scale-95 shadow-sm shadow-slate-200"
                    >
                      <FaEye className="text-[10px]" />
                      Details
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>

          <div className="hidden sm:block overflow-x-auto">
            <table className="min-w-full text-xs sm:text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-1.5 sm:px-2 md:px-3 lg:px-6 py-2 sm:py-3 md:py-4 text-left text-[10px] sm:text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    <span className="hidden sm:inline">Ad ID</span><span className="sm:hidden">ID</span>
                  </th>
                  <th className="px-1.5 sm:px-2 md:px-3 lg:px-6 py-2 sm:py-3 md:py-4 text-left text-[10px] sm:text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    <span className="hidden sm:inline">Ad Type</span><span className="sm:hidden">Type</span>
                  </th>



                  <th className="px-1.5 sm:px-2 md:px-3 lg:px-6 py-2 sm:py-3 md:py-4 text-left text-[10px] sm:text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-1.5 sm:px-2 md:px-3 lg:px-6 py-2 sm:py-3 md:py-4 text-left text-[10px] sm:text-xs font-semibold text-gray-600 uppercase tracking-wider"><span className="hidden sm:inline">Channel Name</span><span className="sm:hidden">Channel</span></th>
                  <th className="px-1.5 sm:px-2 md:px-3 lg:px-6 py-2 sm:py-3 md:py-4 text-left text-[10px] sm:text-xs font-semibold text-gray-600 uppercase tracking-wider">Platform</th>
                  <th className="px-1.5 sm:px-2 md:px-3 lg:px-6 py-2 sm:py-3 md:py-4 text-left text-[10px] sm:text-xs font-semibold text-gray-600 uppercase tracking-wider"><span className="hidden sm:inline">Duration</span><span className="sm:hidden">Dur</span></th>
                  <th className="px-1.5 sm:px-2 md:px-3 lg:px-6 py-2 sm:py-3 md:py-4 text-left text-[10px] sm:text-xs font-semibold text-gray-600 uppercase tracking-wider"><span className="hidden md:inline">Video Link</span><span className="md:hidden hidden sm:inline">Video</span><span className="sm:hidden">V</span></th>
                  <th className="px-1.5 sm:px-2 md:px-3 lg:px-6 py-2 sm:py-3 md:py-4 text-left text-[10px] sm:text-xs font-semibold text-gray-600 uppercase tracking-wider"><span className="hidden sm:inline">Screenshot</span><span className="sm:hidden">SS</span></th>
                  <th className="px-1.5 sm:px-2 md:px-3 lg:px-6 py-2 sm:py-3 md:py-4 text-left text-[10px] sm:text-xs font-semibold text-gray-600 uppercase tracking-wider"><span className="hidden sm:inline">Budget</span><span className="sm:hidden">₹</span></th>
                  <th className="px-1.5 sm:px-2 md:px-3 lg:px-6 py-2 sm:py-3 md:py-4 text-left text-[10px] sm:text-xs font-semibold text-gray-600 uppercase tracking-wider"><span className="hidden sm:inline">Submitted At</span><span className="sm:hidden">Date</span></th>
                  <th className="px-1.5 sm:px-2 md:px-3 lg:px-6 py-2 sm:py-3 md:py-4 text-left text-[10px] sm:text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td
                      colSpan="12"
                      className="px-3 sm:px-4 md:px-6 py-6 sm:py-8 md:py-12 text-center text-gray-500"
                    >
                      <div className="flex flex-col items-center">
                        <div className="animate-spin rounded-full h-6 w-6 sm:h-8 sm:w-8 border-b-2 border-blue-600 mb-2 sm:mb-3 md:mb-4"></div>
                        <p className="text-sm sm:text-base md:text-lg font-medium">
                          Loading advertisements...
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : completedAds.length === 0 ? (
                  <tr>
                    <td colSpan="12" className="px-6 py-20 text-center">
                      <div className="max-w-md mx-auto">
                        <div className="relative inline-block mb-6">
                          <div className="absolute inset-0 bg-purple-400/20 blur-[50px] rounded-full" />
                          <div className="relative w-40 h-40 rounded-full border-4 border-purple-100 shadow-2xl overflow-hidden mx-auto bg-white flex items-center justify-center ring-8 ring-purple-50/50">
                            <FaCheckDouble className="text-purple-100 text-7xl" />
                          </div>
                        </div>
                        <h3 className="text-slate-900 font-black text-2xl tracking-tight mb-2">No Records Found</h3>
                        <p className="text-slate-500 text-sm font-medium mb-8">
                          When your advertisement journey is complete, your achievements will be recorded here. Start your next campaign to grow your success!
                        </p>
                        <button
                          onClick={fetchCompletedAds}
                          className="bg-gradient-to-r from-purple-600 to-indigo-700 text-white px-8 py-3 rounded-2xl font-black text-xs uppercase tracking-widest hover:shadow-xl hover:shadow-purple-200 transition-all active:scale-95"
                        >
                          Refresh Completed List
                        </button>
                      </div>
                    </td>
                  </tr>
                ) : (
                  // Map through the paginated data and display in table rows
                  paginatedAds.map((ad, index) => (
                    <tr
                      key={ad.adId || index}
                      className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}
                    >
                      <td className="px-1.5 sm:px-2 md:px-3 lg:px-6 py-2 sm:py-3 md:py-4 whitespace-nowrap text-[10px] sm:text-xs md:text-sm font-medium text-gray-900 truncate">
                        {ad.adId || "N/A"}
                      </td>
                      <td className="px-1.5 sm:px-2 md:px-3 lg:px-6 py-2 sm:py-3 md:py-4 whitespace-nowrap text-[10px] sm:text-xs md:text-sm text-gray-600">
                        {ad.adType || "N/A"}
                      </td>

                      <td className="px-1.5 sm:px-2 md:px-3 lg:px-6 py-2 sm:py-3 md:py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex px-1.5 sm:px-2 md:px-3 py-0.5 sm:py-1 text-[10px] sm:text-xs font-semibold rounded-full ${ad.status === "Completed"
                            ? "bg-green-100 text-green-800"
                            : ad.status === "In Progress"
                              ? "bg-yellow-100 text-yellow-800"
                              : ad.status === "Pending"
                                ? "bg-orange-100 text-orange-800"
                                : "bg-blue-100 text-blue-800"
                            }`}
                        >
                          {ad.status || "Completed"}
                        </span>
                      </td>
                      {/* Proofs: channelName, platform, duration, videoLink, screenshot,submittedAt */}
                      <td className="px-1.5 sm:px-2 md:px-3 lg:px-6 py-2 sm:py-3 md:py-4 whitespace-nowrap text-[10px] sm:text-xs md:text-sm text-gray-600 truncate max-w-[80px] sm:max-w-none">
                        {ad.proofs && ad.proofs.length > 0 ? ad.proofs.map((proof, i) => (
                          <div key={i}>{proof.channelName || "N/A"}</div>
                        )) : "N/A"}
                      </td>
                      <td className="px-1.5 sm:px-2 md:px-3 lg:px-6 py-2 sm:py-3 md:py-4 whitespace-nowrap text-[10px] sm:text-xs md:text-sm text-gray-600">
                        {ad.proofs && ad.proofs.length > 0 ? ad.proofs.map((proof, i) => (
                          <div key={i}>{proof.platform || "N/A"}</div>
                        )) : "N/A"}
                      </td>
                      <td className="px-1.5 sm:px-2 md:px-3 lg:px-6 py-2 sm:py-3 md:py-4 whitespace-nowrap text-[10px] sm:text-xs md:text-sm text-gray-600">
                        {ad.proofs && ad.proofs.length > 0 ? ad.proofs.map((proof, i) => (
                          <div key={i}>{proof.duration || "N/A"}</div>
                        )) : "N/A"}
                      </td>
                      <td className="px-1.5 sm:px-2 md:px-3 lg:px-6 py-2 sm:py-3 md:py-4 whitespace-nowrap text-[10px] sm:text-xs md:text-sm text-blue-600">
                        {ad.proofs && ad.proofs.length > 0 ? ad.proofs.map((proof, i) => (
                          proof.videoLink ? (
                            <div key={i}>
                              <a href={proof.videoLink} target="_blank" rel="noopener noreferrer" className="underline text-blue-600"><span className="hidden sm:inline">Video</span><span className="sm:hidden">V</span></a>
                            </div>
                          ) : "N/A"
                        )) : "N/A"}
                      </td>
                      <td className="px-1.5 sm:px-2 md:px-3 lg:px-6 py-2 sm:py-3 md:py-4 whitespace-nowrap text-[10px] sm:text-xs md:text-sm">
                        {ad.proofs && ad.proofs.length > 0 ? ad.proofs.map((proof, i) => (
                          proof.screenshot ? (
                            <div key={i}>
                              <img src={proof.screenshot.startsWith('http') ? proof.screenshot : `${process.env.REACT_APP_API_BASE_URL}/${proof.screenshot.replace('\\', '/')}`}
                                alt="Screenshot" className="h-8 w-8 sm:h-10 sm:w-10 md:h-12 md:w-12 object-cover rounded" />
                            </div>
                          ) : "N/A"
                        )) : "N/A"}
                      </td>
                      <td className="px-1.5 sm:px-2 md:px-3 lg:px-6 py-2 sm:py-3 md:py-4 whitespace-nowrap text-[10px] sm:text-xs md:text-sm text-gray-600">
                        ₹{ad.finalReporterPrice ? Number(ad.finalReporterPrice).toLocaleString() : "0"}
                      </td>
                      <td className="px-1.5 sm:px-2 md:px-3 lg:px-6 py-2 sm:py-3 md:py-4 whitespace-nowrap text-[10px] sm:text-xs md:text-sm text-gray-600">
                        {ad.proofs && ad.proofs.length > 0 && ad.proofs[0].submittedAt
                          ? (() => {
                            const dateObj = new Date(ad.proofs[0].submittedAt);
                            const date = dateObj.toLocaleDateString('en-IN', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric'
                            });
                            const time = dateObj.toLocaleTimeString('en-IN', {
                              hour: '2-digit',
                              minute: '2-digit',
                              hour12: true
                            });
                            return (
                              <span>
                                <span className="hidden sm:inline">{date}</span><span className="sm:hidden">{dateObj.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit' })}</span><br />
                                <span className="text-[8px] sm:text-xs text-gray-500">{time}</span>
                              </span>
                            );
                          })()
                          : "N/A"}
                      </td>
                      <td className="px-1.5 sm:px-2 md:px-3 lg:px-6 py-2 sm:py-3 md:py-4 whitespace-nowrap text-[10px] sm:text-xs md:text-sm">
                        <button
                          onClick={() => openDetailsModal(ad)}
                          className="inline-flex items-center px-2 sm:px-3 py-1 sm:py-1.5 bg-blue-600 text-white text-[10px] sm:text-xs font-medium rounded hover:bg-blue-700 transition-colors duration-200"
                        >
                          <FaEye className="mr-0.5 sm:mr-1 w-2.5 h-2.5 sm:w-3 sm:h-3" />
                          <span className="hidden sm:inline">View Details</span><span className="sm:hidden">View</span>
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="flex justify-center mt-3 sm:mt-4 md:mt-6 px-2 sm:px-4">
              <nav className="inline-flex items-center gap-1 sm:gap-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`px-2 sm:px-3 md:px-4 py-1 sm:py-1.5 rounded border border-yellow-400 text-yellow-800 bg-white hover:bg-yellow-100 transition-colors text-xs sm:text-sm ${currentPage === 1 ? 'opacity-50 cursor-not-allowed' : ''}`}
                  aria-label="Previous Page"
                >
                  <span className="hidden sm:inline">Previous</span><span className="sm:hidden">Prev</span>
                </button>
                <span className="px-3 sm:px-4 md:px-6 py-1 sm:py-1.5 rounded border border-yellow-400 bg-yellow-300 text-yellow-900 font-bold text-sm sm:text-base md:text-lg min-w-[32px] sm:min-w-[40px] md:min-w-[48px] text-center">
                  {currentPage}
                </span>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className={`px-2 sm:px-3 md:px-4 py-1 sm:py-1.5 rounded border border-yellow-400 text-yellow-800 bg-white hover:bg-yellow-100 transition-colors text-xs sm:text-sm ${currentPage === totalPages ? 'opacity-50 cursor-not-allowed' : ''}`}
                  aria-label="Next Page"
                >
                  Next
                </button>
              </nav>
            </div>
          )}
        </div>
      </div>

      {/* Details Modal */}
      {showDetailsModal && selectedAd && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-2 sm:p-4">
          <div className="bg-white rounded-lg sm:rounded-xl md:rounded-2xl shadow-2xl w-full max-w-6xl max-h-[95vh] overflow-y-auto relative border border-gray-200">
            {/* Header */}
            <div className="bg-gradient-to-r from-green-600 via-emerald-600 to-teal-700 text-white p-3 sm:p-4 md:p-6 rounded-t-lg sm:rounded-t-xl md:rounded-t-2xl relative">
              <button
                className="absolute top-2 sm:top-3 md:top-4 right-2 sm:right-3 md:right-4 text-white hover:text-gray-200 text-xl sm:text-2xl font-bold bg-white bg-opacity-20 rounded-full w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 flex items-center justify-center transition-colors"
                onClick={closeDetailsModal}
                title="Close"
              >
                <FaTimes className="w-3 h-3 sm:w-4 sm:h-4" />
              </button>
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="bg-white bg-opacity-20 rounded-lg p-1.5 sm:p-2">
                  <FaCheckCircle className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-green-300" />
                </div>
                <div>
                  <h3 className="text-base sm:text-lg md:text-xl lg:text-2xl font-bold">Completed Advertisement Details</h3>
                  <p className="text-green-100 text-xs sm:text-sm">Complete information about your completed advertisement work</p>
                </div>
              </div>
            </div>

            <div className="p-4 sm:p-6 md:p-8">
              {/* Advertisement Overview */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 md:gap-6 mb-4 sm:mb-6 md:mb-8">
                <div className="bg-gradient-to-br from-blue-50 to-indigo-100 p-3 sm:p-4 md:p-6 rounded-lg sm:rounded-xl border border-blue-200">
                  <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                    <div className="bg-blue-500 text-white rounded-lg p-1.5 sm:p-2">
                      <FaAd className="w-3 h-3 sm:w-4 sm:h-4" />
                    </div>
                    <h4 className="font-semibold text-blue-800 text-xs sm:text-sm md:text-base">Advertisement ID</h4>
                  </div>
                  <p className="text-blue-700 font-mono text-xs sm:text-sm">{selectedAd.adId}</p>
                </div>

                <div className="bg-gradient-to-br from-purple-50 to-pink-100 p-3 sm:p-4 md:p-6 rounded-lg sm:rounded-xl border border-purple-200">
                  <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                    <div className="bg-purple-500 text-white rounded-lg p-1.5 sm:p-2">
                      <FaTag className="w-3 h-3 sm:w-4 sm:h-4" />
                    </div>
                    <h4 className="font-semibold text-purple-800 text-xs sm:text-sm md:text-base">Ad Type</h4>
                  </div>
                  <p className="text-purple-700 font-medium text-xs sm:text-sm md:text-base">{selectedAd.adType}</p>
                </div>

                <div className="bg-gradient-to-br from-green-50 to-emerald-100 p-3 sm:p-4 md:p-6 rounded-lg sm:rounded-xl border border-green-200">
                  <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                    <div className="bg-green-500 text-white rounded-lg p-1.5 sm:p-2">
                      <FaMoneyBillWave className="w-3 h-3 sm:w-4 sm:h-4" />
                    </div>
                    <h4 className="font-semibold text-green-800 text-xs sm:text-sm md:text-base">Earnings</h4>
                  </div>
                  <p className="text-green-700 font-bold text-sm sm:text-base md:text-lg">₹{selectedAd.finalReporterPrice ? Number(selectedAd.finalReporterPrice).toLocaleString() : "0"}</p>
                </div>
              </div>

              {/* Proof Details */}
              {selectedAd.proofs && selectedAd.proofs.length > 0 && (
                <div className="bg-gradient-to-br from-gray-50 to-gray-100 p-3 sm:p-4 md:p-6 rounded-lg sm:rounded-xl md:rounded-2xl border border-gray-200 mb-4 sm:mb-6 md:mb-8">
                  <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4 md:mb-6">
                    <div className="bg-gray-500 text-white rounded-lg p-1.5 sm:p-2">
                      <FaVideo className="w-3 h-3 sm:w-4 sm:h-4" />
                    </div>
                    <div>
                      <h5 className="font-semibold text-gray-800 text-xs sm:text-sm md:text-base">Proof Submission Details</h5>
                      <p className="text-xs sm:text-sm text-gray-600">Your submitted proof and completion details</p>
                    </div>
                  </div>

                  {selectedAd.proofs.map((proof, index) => (
                    <div key={index} className="bg-white rounded-lg sm:rounded-xl p-3 sm:p-4 md:p-6 border border-gray-200 mb-3 sm:mb-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 md:gap-6">
                        {/* Left Column - Basic Info */}
                        <div className="space-y-2 sm:space-y-3 md:space-y-4">
                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-0.5 sm:mb-1">Channel Name</label>
                            <p className="text-gray-900 font-medium text-xs sm:text-sm md:text-base">{proof.channelName || "N/A"}</p>
                          </div>

                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-0.5 sm:mb-1">Platform</label>
                            <div className="flex items-center gap-1.5 sm:gap-2">
                              <FaGlobe className="w-3 h-3 sm:w-4 sm:h-4 text-blue-500" />
                              <p className="text-gray-900 font-medium text-xs sm:text-sm md:text-base">{proof.platform || "N/A"}</p>
                            </div>
                          </div>

                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-0.5 sm:mb-1">Duration</label>
                            <div className="flex items-center gap-1.5 sm:gap-2">
                              <FaClock className="w-3 h-3 sm:w-4 sm:h-4 text-orange-500" />
                              <p className="text-gray-900 font-medium text-xs sm:text-sm md:text-base">{proof.duration || "N/A"}</p>
                            </div>
                          </div>

                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-0.5 sm:mb-1">Video Link</label>
                            {proof.videoLink ? (
                              <a
                                href={proof.videoLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 sm:gap-2 text-blue-600 hover:text-blue-800 font-medium text-xs sm:text-sm"
                              >
                                <FaPlay className="w-3 h-3 sm:w-4 sm:h-4" />
                                <span className="hidden sm:inline">Watch Video</span><span className="sm:hidden">Video</span>
                                <FaExternalLinkAlt className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                              </a>
                            ) : (
                              <p className="text-gray-500 text-xs sm:text-sm">N/A</p>
                            )}
                          </div>
                        </div>

                        {/* Right Column - Screenshots and Dates */}
                        <div className="space-y-2 sm:space-y-3 md:space-y-4">
                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Initial Proof Screenshot</label>
                            {proof.screenshot ? (
                              <div className="relative">
                                <img
                                  src={getScreenshotUrl(proof.screenshot)}
                                  alt="Initial Proof Screenshot"
                                  className="w-full h-24 sm:h-28 md:h-32 object-cover rounded-lg border border-gray-200 cursor-pointer hover:opacity-80 transition-opacity"
                                  onClick={() => openScreenshotModal(getScreenshotUrl(proof.screenshot))}
                                />
                                <button
                                  onClick={() => openScreenshotModal(getScreenshotUrl(proof.screenshot))}
                                  className="absolute top-1 sm:top-2 right-1 sm:right-2 bg-black bg-opacity-50 text-white p-0.5 sm:p-1 rounded hover:bg-opacity-70 transition-colors"
                                >
                                  <FaCamera className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                                </button>
                              </div>
                            ) : (
                              <p className="text-gray-500 text-xs sm:text-sm">No screenshot available</p>
                            )}
                          </div>

                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Completion Screenshot</label>
                            {proof.completedTaskScreenshot ? (
                              <div className="relative">
                                <img
                                  src={getScreenshotUrl(proof.completedTaskScreenshot)}
                                  alt="Completion Screenshot"
                                  className="w-full h-24 sm:h-28 md:h-32 object-cover rounded-lg border border-gray-200 cursor-pointer hover:opacity-80 transition-opacity"
                                  onClick={() => openScreenshotModal(getScreenshotUrl(proof.completedTaskScreenshot))}
                                />
                                <button
                                  onClick={() => openScreenshotModal(getScreenshotUrl(proof.completedTaskScreenshot))}
                                  className="absolute top-1 sm:top-2 right-1 sm:right-2 bg-black bg-opacity-50 text-white p-0.5 sm:p-1 rounded hover:bg-opacity-70 transition-colors"
                                >
                                  <FaCamera className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                                </button>
                              </div>
                            ) : (
                              <p className="text-gray-500 text-xs sm:text-sm">No completion screenshot available</p>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Timeline */}
                      <div className="mt-3 sm:mt-4 md:mt-6 pt-3 sm:pt-4 md:pt-6 border-t border-gray-200">
                        <h6 className="font-medium text-gray-700 mb-2 sm:mb-3 flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm md:text-base">
                          <FaCalendar className="w-3 h-3 sm:w-4 sm:h-4 text-blue-500" />
                          Timeline
                        </h6>
                        <div className="space-y-1.5 sm:space-y-2 text-xs sm:text-sm">
                          {proof.submittedAt && (
                            <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-0">
                              <span className="text-gray-600">Initial Proof Submitted:</span>
                              <span className="font-medium">{formatDate(proof.submittedAt)}</span>
                            </div>
                          )}
                          {proof.completionSubmittedAt && (
                            <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-0">
                              <span className="text-gray-600">Completion Screenshot Submitted:</span>
                              <span className="font-medium">{formatDate(proof.completionSubmittedAt)}</span>
                            </div>
                          )}
                          {proof.completedAt && (
                            <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-0">
                              <span className="text-gray-600">Admin Approved:</span>
                              <span className="font-medium text-green-600">{formatDate(proof.completedAt)}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Status Information */}
              <div className="bg-gradient-to-br from-green-50 to-emerald-100 p-3 sm:p-4 md:p-6 rounded-lg sm:rounded-xl md:rounded-2xl border border-green-200">
                <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3 md:mb-4">
                  <div className="bg-green-500 text-white rounded-lg p-1.5 sm:p-2">
                    <FaCheckCircle className="w-3 h-3 sm:w-4 sm:h-4" />
                  </div>
                  <h5 className="font-semibold text-green-800 text-xs sm:text-sm md:text-base">Completion Status</h5>
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                  <div className="bg-green-100 text-green-800 px-2 sm:px-3 md:px-4 py-1 sm:py-1.5 md:py-2 rounded-full font-medium text-xs sm:text-sm">
                    ✅ Completed Successfully
                  </div>
                  <p className="text-green-700 text-xs sm:text-sm md:text-base">
                    Your advertisement work has been completed and approved by the admin.
                  </p>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="bg-gray-50 px-4 sm:px-6 md:px-8 py-2 sm:py-3 md:py-4 rounded-b-lg sm:rounded-b-xl md:rounded-b-2xl border-t border-gray-200">
              <div className="flex justify-end">
                <button
                  onClick={closeDetailsModal}
                  className="px-3 sm:px-4 md:px-6 py-1.5 sm:py-2 bg-gray-600 hover:bg-gray-700 text-white rounded text-xs sm:text-sm md:text-base font-medium transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Screenshot Modal */}
      {showScreenshotModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-2 sm:p-4">
          <div className="bg-white rounded-lg sm:rounded-xl md:rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto relative">
            <button
              className="absolute top-2 sm:top-3 md:top-4 right-2 sm:right-3 md:right-4 text-white hover:text-gray-200 text-xl sm:text-2xl font-bold bg-black bg-opacity-50 rounded-full w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 flex items-center justify-center transition-colors z-10"
              onClick={closeScreenshotModal}
              title="Close"
            >
              <FaTimes className="w-3 h-3 sm:w-4 sm:h-4" />
            </button>
            <div className="p-2 sm:p-3 md:p-4">
              <img
                src={currentScreenshot}
                alt="Screenshot"
                className="max-w-full h-auto rounded-lg shadow-lg"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = "/fallback-image.jpg";
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InfluenerCompletedLeads;
